"use client";

import { motion } from "framer-motion";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import Image from "next/image";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { LoginResult } from "@/actions/serverActions/auth/loginAction";
import {
  type VerifyLoginTwoFactorResult,
  verifyLoginTwoFactorAction,
} from "@/actions/serverActions/auth/verifyLoginTwoFactorAction";
import logo from "@/assets/images/CashlessAi-Logo.png";
import ResetPasswordModal from "@/components/forgot-password/ResetPasswordModal";
import { getFullPath } from "@/config/basePath";
import { useAuth } from "@/hooks/useAuth";
import { useServerAction } from "@/hooks/useServerAction";
import { cn } from "@/lib/utils";
import {
  setEmail,
  setPassword,
} from "@/store/Features/states/auth-management/authSlice";
import { useAppSelector } from "@/store/hooks";

interface TwoFactorChallenge {
  token: string;
  /**
   * Epoch ms when the challenge dies.
   *
   * Derived from the login response's `expires_in` (**seconds**, 300) — the
   * gateway sends a duration, not a timestamp, so it is resolved to a deadline
   * once here rather than re-derived on every tick.
   */
  expiresAt: number;
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export type LoginFormProps = React.ComponentProps<"div">;

export function LoginForm({ className, ...props }: LoginFormProps) {
  const { email, password, handleLogin, dispatch } = useAuth();
  const { isLoading } = useAppSelector((state) => state.auth);
  const verify = useServerAction(verifyLoginTwoFactorAction);

  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [remember, setRemember] = useState<boolean>(() => !!email);
  const [showPassword, setShowPassword] = useState(false);

  // ── 2FA challenge state ──────────────────────────────────────────────────
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [twoFactorMode, setTwoFactorMode] = useState<"totp" | "backup">("totp");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorError, setTwoFactorError] = useState<string | undefined>();
  const [isVerifying, setIsVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (email) setRemember(true);
  }, [email]);

  /**
   * Countdown for the challenge window.
   *
   * auth-gateway returns `expires_in` **seconds** (300), not an absolute
   * timestamp, so the deadline is computed once when the challenge arrives.
   */
  useEffect(() => {
    if (!challenge) return;
    setIsExpired(false);
    const target = challenge.expiresAt;
    const tick = () => {
      const remaining = Math.max(0, Math.round((target - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) setIsExpired(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [challenge]);

  // Focus the code input when the 2FA step appears
  useEffect(() => {
    if (challenge) {
      setTimeout(() => codeInputRef.current?.focus(), 50);
    }
  }, [challenge, twoFactorMode]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await handleLogin(remember);
    if (result && "requiresTwoFactor" in result && result.requiresTwoFactor) {
      const r = result as LoginResult;
      if (r.challengeToken) {
        setChallenge({
          token: r.challengeToken,
          // `challengeExpiresIn` is seconds; 300 is the documented default, used
          // as the floor if the field is ever absent
          expiresAt: Date.now() + (r.challengeExpiresIn ?? 300) * 1000,
        });
        setTwoFactorCode("");
        setTwoFactorError(undefined);
        setTwoFactorMode("totp");
      }
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge || isExpired) return;

    setIsVerifying(true);
    setTwoFactorError(undefined);

    // One `code` field for both an authenticator code and a backup code — the
    // gateway does not distinguish them on the request, so the mode only
    // changes the label and the input's length hint
    const result = await verify({
      challengeToken: challenge.token,
      code: twoFactorCode,
      email,
    });
    setIsVerifying(false);

    if (result.success) {
      window.location.href = getFullPath("/");
      return;
    }

    const r = result as VerifyLoginTwoFactorResult;
    setTwoFactorError(r.message);
    if (r.isChallengeDead) {
      setChallenge(null);
    } else {
      setTwoFactorCode("");
      codeInputRef.current?.focus();
    }
  };

  const cancelChallenge = () => {
    setChallenge(null);
    setTwoFactorCode("");
    setTwoFactorError(undefined);
  };

  const switchMode = (mode: "totp" | "backup") => {
    setTwoFactorMode(mode);
    setTwoFactorCode("");
    setTwoFactorError(undefined);
  };

  const inputClass =
    "w-full px-4 py-3.5 border-2 border-transparent rounded-lg bg-primary-500/10 focus:border-primary-500 focus:bg-primary-500/10 outline-none transition text-sm dark:bg-gray-900";

  return (
    <div
      className={cn("flex items-center justify-center p-4 rounded", className)}
      {...props}
    >
      <motion.div
        className="w-full max-w-md"
        initial={{ x: "calc(50% - 2px)" }}
        animate={{ x: 0 }}
        transition={{
          duration: 0.75,
          delay: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
      >
        <div className="relative flex">
          <div className="w-1 bg-primary-500 shrink-0 z-10 rounded" />

          <div className="overflow-hidden flex-1">
            <motion.div
              className="pl-10"
              initial={{ x: "calc(-100% - 32px)" }}
              animate={{ x: 0 }}
              transition={{
                duration: 0.75,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              {/* Logo */}
              <div className="mb-5">
                <Image
                  src={logo}
                  alt="Logo"
                  width={150}
                  height={50}
                  className="block w-[350px] h-auto object-contain scale-[1.2] ml-1"
                  priority
                />
              </div>

              {challenge ? (
                /* ── Step 2: 2FA verification ─────────────────────────── */
                <div>
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck size={22} className="text-primary-500" />
                      <h2 className="font-goldman text-2xl font-bold text-foreground">
                        Two-step verification
                      </h2>
                    </div>
                    <p className="text-gray-500 text-sm">
                      Signing in as{" "}
                      <span className="font-medium text-foreground">
                        {email}
                      </span>
                    </p>
                  </div>

                  {/* Mode tabs */}
                  <div className="flex gap-1 mb-5 p-1 rounded-lg bg-primary-500/10">
                    <button
                      type="button"
                      onClick={() => switchMode("totp")}
                      className={cn(
                        "flex-1 py-2 text-xs font-medium rounded-md transition",
                        twoFactorMode === "totp"
                          ? "bg-white text-primary-500 shadow-sm dark:bg-gray-800"
                          : "text-gray-500 hover:text-foreground",
                      )}
                    >
                      Authenticator app
                    </button>
                    <button
                      type="button"
                      onClick={() => switchMode("backup")}
                      className={cn(
                        "flex-1 py-2 text-xs font-medium rounded-md transition",
                        twoFactorMode === "backup"
                          ? "bg-white text-primary-500 shadow-sm dark:bg-gray-800"
                          : "text-gray-500 hover:text-foreground",
                      )}
                    >
                      Backup code
                    </button>
                  </div>

                  <form onSubmit={onVerify} className="space-y-4">
                    <div>
                      <input
                        ref={codeInputRef}
                        type={twoFactorMode === "totp" ? "text" : "text"}
                        inputMode={
                          twoFactorMode === "totp" ? "numeric" : "text"
                        }
                        autoComplete={
                          twoFactorMode === "totp" ? "one-time-code" : "off"
                        }
                        placeholder={
                          twoFactorMode === "totp"
                            ? "6-digit code"
                            : "XXXX-XXXX"
                        }
                        maxLength={twoFactorMode === "totp" ? 6 : 9}
                        value={twoFactorCode}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (twoFactorMode === "totp") {
                            setTwoFactorCode(v.replace(/\D/g, "").slice(0, 6));
                          } else {
                            setTwoFactorCode(
                              v
                                .toUpperCase()
                                .replace(/[^A-Z0-9-]/g, "")
                                .slice(0, 9),
                            );
                          }
                        }}
                        disabled={isExpired}
                        className={cn(
                          inputClass,
                          "tracking-widest font-mono text-base",
                        )}
                      />

                      {/*
                        Error feedback. There is deliberately NO "n attempts
                        left" counter: auth-gateway does not return a remaining
                        count, and the two bounds that exist (5 per challenge,
                        10 per account) are not interchangeable — showing either
                        as "attempts left" would be wrong half the time.
                      */}
                      {twoFactorError && (
                        <p className="mt-1.5 text-xs text-red-500">
                          {twoFactorError}
                        </p>
                      )}
                    </div>

                    {/* Countdown */}
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>
                        {isExpired ? (
                          <span className="text-red-500 font-medium">
                            Code expired
                          </span>
                        ) : (
                          <>
                            Expires in{" "}
                            <span
                              className={cn(
                                "font-mono font-medium",
                                secondsLeft <= 60
                                  ? "text-red-500"
                                  : "text-foreground",
                              )}
                            >
                              {formatCountdown(secondsLeft)}
                            </span>
                          </>
                        )}
                      </span>
                    </div>

                    <div className="pt-4 space-y-3">
                      <button
                        type="submit"
                        disabled={
                          isVerifying ||
                          isExpired ||
                          (twoFactorMode === "totp"
                            ? twoFactorCode.length !== 6
                            : twoFactorCode.length !== 9)
                        }
                        className="w-[70%] mx-auto block border border-primary-500/40 text-primary-500 py-3 rounded-lg text-lg font-normal hover:bg-primary-500 hover:text-white hover:border-primary-500 transition duration-200 disabled:opacity-60 disabled:pointer-events-none cursor-pointer"
                      >
                        {isVerifying ? "Verifying..." : "Verify"}
                      </button>

                      <button
                        type="button"
                        onClick={cancelChallenge}
                        className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition"
                      >
                        ← Use a different account
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                /* ── Step 1: email + password ─────────────────────────── */
                <div>
                  <div className="mb-10">
                    <h2 className="font-goldman text-3xl font-bold text-foreground mb-2">
                      Welcome Back
                    </h2>
                    <p className="text-gray-500">Sign in to your account</p>
                  </div>

                  <form onSubmit={onSubmit} className="space-y-5">
                    <div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => dispatch(setEmail(e.target.value))}
                        placeholder="Email"
                        autoComplete="email"
                        required
                        className={inputClass}
                      />
                    </div>

                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => dispatch(setPassword(e.target.value))}
                        placeholder="Password"
                        autoComplete="current-password"
                        required
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute inset-y-0 right-3 flex items-center z-10 text-muted-foreground hover:text-foreground focus:outline-none"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                          className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-600">
                          Remember me
                        </span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsForgotOpen(true)}
                        className="text-sm text-primary-500 font-medium transition cursor-pointer hover:underline focus:outline-none"
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <div className="pt-6">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-[70%] mx-auto block border border-primary-500/40 text-primary-500 py-3 rounded-lg text-lg font-normal hover:bg-primary-500 hover:text-white hover:border-primary-500 transition duration-200 disabled:opacity-60 disabled:pointer-events-none cursor-pointer"
                      >
                        {isLoading ? "Logging in..." : "Login"}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </motion.div>

      <ResetPasswordModal
        isOpen={isForgotOpen}
        onClose={() => setIsForgotOpen(false)}
        initialEmail={email}
      />
    </div>
  );
}
